/**
 * Form ↔ payload mapping and validation for the superadmin catalog (products,
 * components, record-key patterns, clients, environments, tenants). Pure;
 * errors are i18n keys under `catalog.errors.*`. The API re-validates all of it.
 */
import type {
  CatalogProduct,
  ClientDetail,
  ClientEnvironment,
  ClientInput,
  EnvKind,
  EnvironmentInput,
  KeyNormalizer,
  KeyPattern,
  KeyPatternInput,
  ProductInput,
  Tenant,
  TenantInput,
} from '@/api/catalogApi'

export type Errors<K extends string> = Partial<Record<K, string>>

const E = (k: string) => `catalog.errors.${k}`

export const PRODUCT_KEY = /^[a-z][a-z0-9_]{0,31}$/
export const COMPONENT_KEY = /^[a-z][a-z0-9_]{0,63}$/
export const PATTERN_TYPE = /^[a-z][a-z0-9_]{0,31}$/
export const SLUG = /^[a-z0-9][a-z0-9-]{0,62}$/
export const TENANT_CODE = /^[A-Z0-9][A-Z0-9_-]{0,15}$/
const DOMAIN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

export const NORMALIZERS: KeyNormalizer[] = ['none', 'upper', 'strip_spaces_upper']
export const ENV_KINDS: EnvKind[] = ['production', 'validation', 'test', 'training']

/** lower_snake key from a display name ("Release labels" → "release_labels"). */
export function keyFromName(name: string, max = 32): string {
  const k = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9_]+/, '')
  return k.slice(0, max)
}

/** kebab slug from a client name ("Mayo Clinic CTL" → "mayo-clinic-ctl"). */
export function slugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
}

export function isTimeZone(tz: string): boolean {
  if (!tz.trim()) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() })
    return true
  } catch {
    return false
  }
}

function isHttpsUrl(v: string): boolean {
  try {
    return new URL(v).protocol === 'https:'
  } catch {
    return false
  }
}

// ─── Product ─────────────────────────────────────────────────────────────────

export interface ProductForm {
  key: string
  name: string
  description: string
  slaTargetPct: string
  active: boolean
}

export const EMPTY_PRODUCT_FORM: ProductForm = { key: '', name: '', description: '', slaTargetPct: '95', active: true }

export function productToForm(p: CatalogProduct): ProductForm {
  return { key: p.key, name: p.name, description: p.description ?? '', slaTargetPct: String(p.slaTargetPct), active: p.active }
}

export function validateProduct(f: ProductForm): Errors<'key' | 'name' | 'slaTargetPct'> {
  const e: Errors<'key' | 'name' | 'slaTargetPct'> = {}
  if (!f.name.trim()) e.name = E('required')
  if (!PRODUCT_KEY.test(f.key)) e.key = E('key')
  const n = Number(f.slaTargetPct)
  if (!/^\s*\d+\s*$/.test(f.slaTargetPct) || n < 0 || n > 100) e.slaTargetPct = E('percent')
  return e
}

/** Create payload, or — given the current product — only the changed fields. */
export function productPayload(f: ProductForm, current?: CatalogProduct): ProductInput {
  const full: Required<ProductInput> = {
    key: f.key,
    name: f.name.trim(),
    description: f.description.trim() || null,
    slaTargetPct: Number(f.slaTargetPct),
    active: f.active,
  }
  if (!current) return { key: full.key, name: full.name, description: full.description ?? undefined, slaTargetPct: full.slaTargetPct }
  const out: ProductInput = {}
  if (full.key !== current.key) out.key = full.key
  if (full.name !== current.name) out.name = full.name
  if (full.description !== (current.description ?? null)) out.description = full.description
  if (full.slaTargetPct !== current.slaTargetPct) out.slaTargetPct = full.slaTargetPct
  if (full.active !== current.active) out.active = full.active
  return out
}

// ─── Component ───────────────────────────────────────────────────────────────

export function validateComponent(f: { key: string; name: string }, existingKeys: string[]): Errors<'key' | 'name'> {
  const e: Errors<'key' | 'name'> = {}
  if (!f.name.trim()) e.name = E('required')
  if (!COMPONENT_KEY.test(f.key)) e.key = E('key')
  else if (existingKeys.includes(f.key)) e.key = E('duplicateKey')
  return e
}

// ─── Record-key pattern ──────────────────────────────────────────────────────

export interface KeyPatternForm {
  type: string
  regex: string
  sample: string
  normalizer: KeyNormalizer
  urlTemplate: string
}

export const EMPTY_PATTERN_FORM: KeyPatternForm = { type: '', regex: '', sample: '', normalizer: 'none', urlTemplate: '' }

export function patternToForm(p: KeyPattern): KeyPatternForm {
  return {
    type: p.type,
    regex: p.regex,
    sample: p.sample ?? '',
    normalizer: (NORMALIZERS as string[]).includes(p.normalizer) ? (p.normalizer as KeyNormalizer) : 'none',
    urlTemplate: p.urlTemplate ?? '',
  }
}

/** Mirrors api/src/tickets/record-key.ts (search normalization). */
export function normalizeKey(key: string, normalizer: string): string {
  const trimmed = key.trim()
  if (normalizer === 'strip_spaces_upper') return trimmed.replace(/[\s-]+/g, '').toUpperCase()
  if (normalizer === 'upper') return trimmed.toUpperCase()
  return trimmed
}

export interface PatternTest {
  /** null when the regex does not compile. */
  matches: boolean | null
  regexError: string | null
  normalized: string
  url: string | null
}

/** The live tester: does `key` match, what it normalizes to, and the record URL (same rule as link-record). */
export function testPattern(f: Pick<KeyPatternForm, 'regex' | 'normalizer' | 'urlTemplate'>, key: string): PatternTest {
  let re: RegExp | null = null
  let regexError: string | null = null
  try {
    re = new RegExp(f.regex)
  } catch (err) {
    regexError = err instanceof Error ? err.message : String(err)
  }
  const trimmed = key.trim()
  return {
    matches: re ? re.test(trimmed) : null,
    regexError,
    normalized: normalizeKey(key, f.normalizer),
    url: f.urlTemplate.trim() && trimmed ? f.urlTemplate.trim().replace('{key}', encodeURIComponent(trimmed)) : null,
  }
}

export function validatePattern(f: KeyPatternForm, existingTypes: string[]): Errors<'type' | 'regex' | 'sample' | 'urlTemplate'> {
  const e: Errors<'type' | 'regex' | 'sample' | 'urlTemplate'> = {}
  if (!PATTERN_TYPE.test(f.type)) e.type = E('key')
  else if (existingTypes.includes(f.type)) e.type = E('duplicateType')
  const test = testPattern(f, f.sample)
  if (!f.regex) e.regex = E('required')
  else if (test.regexError) e.regex = E('regex')
  if (!f.sample.trim()) e.sample = E('required')
  else if (test.matches === false) e.sample = E('sampleNoMatch')
  const url = f.urlTemplate.trim()
  if (url) {
    if (!url.includes('{key}')) e.urlTemplate = E('keyPlaceholder')
    else if (!isHttpsUrl(url.replace('{key}', 'KEY'))) e.urlTemplate = E('https')
  }
  return e
}

export function patternPayload(f: KeyPatternForm): KeyPatternInput {
  return {
    type: f.type,
    regex: f.regex,
    sample: f.sample.trim(),
    normalizer: f.normalizer,
    urlTemplate: f.urlTemplate.trim() || null,
  }
}

// ─── Client ──────────────────────────────────────────────────────────────────

export interface ClientForm {
  name: string
  slug: string
  timeZone: string
  emailDomains: string[]
}

export const EMPTY_CLIENT_FORM: ClientForm = { name: '', slug: '', timeZone: 'America/New_York', emailDomains: [] }

export function clientToForm(c: ClientDetail): ClientForm {
  return { name: c.name, slug: c.slug, timeZone: c.timeZone, emailDomains: [...c.emailDomains] }
}

/**
 * Adds typed domains (comma / space separated, "@mayo.edu" accepted) to the
 * chip list. Returns the new list and the entries that are not domains.
 */
export function addDomains(current: string[], typed: string): { domains: string[]; invalid: string[] } {
  const domains = [...current]
  const invalid: string[] = []
  for (const raw of typed.split(/[\s,;]+/)) {
    const d = raw.trim().toLowerCase().replace(/^@/, '')
    if (!d) continue
    if (!DOMAIN.test(d)) invalid.push(raw.trim())
    else if (!domains.includes(d)) domains.push(d)
  }
  return { domains, invalid }
}

export function validateClient(f: ClientForm): Errors<'name' | 'slug' | 'timeZone'> {
  const e: Errors<'name' | 'slug' | 'timeZone'> = {}
  if (!f.name.trim()) e.name = E('required')
  if (!SLUG.test(f.slug)) e.slug = E('slug')
  if (!isTimeZone(f.timeZone)) e.timeZone = E('timeZone')
  return e
}

export function clientPayload(f: ClientForm, current?: ClientDetail): ClientInput {
  const full = { name: f.name.trim(), slug: f.slug, timeZone: f.timeZone.trim(), emailDomains: f.emailDomains }
  if (!current) return full
  const out: ClientInput = {}
  if (full.name !== current.name) out.name = full.name
  if (full.slug !== current.slug) out.slug = full.slug
  if (full.timeZone !== current.timeZone) out.timeZone = full.timeZone
  if (full.emailDomains.join(',') !== current.emailDomains.join(',')) out.emailDomains = full.emailDomains
  return out
}

// ─── Environment ─────────────────────────────────────────────────────────────

export interface EnvironmentForm {
  productId: string
  name: string
  kind: EnvKind
  isValidated: boolean
  baseUrl: string
}

export const EMPTY_ENV_FORM: EnvironmentForm = { productId: '', name: 'Prod', kind: 'production', isValidated: true, baseUrl: '' }

export function envToForm(e: ClientEnvironment): EnvironmentForm {
  return { productId: e.productId, name: e.name, kind: e.kind, isValidated: e.isValidated, baseUrl: e.baseUrl ?? '' }
}

export function validateEnvironment(f: EnvironmentForm): Errors<'productId' | 'name' | 'baseUrl'> {
  const e: Errors<'productId' | 'name' | 'baseUrl'> = {}
  if (!f.productId) e.productId = E('required')
  if (!f.name.trim()) e.name = E('required')
  if (f.baseUrl.trim() && !isHttpsUrl(f.baseUrl.trim())) e.baseUrl = E('https')
  return e
}

export function environmentPayload(f: EnvironmentForm, editing: boolean): EnvironmentInput {
  const base: EnvironmentInput = { name: f.name.trim(), kind: f.kind, isValidated: f.isValidated }
  if (editing) return { ...base, baseUrl: f.baseUrl.trim() || null }
  return { ...base, productId: f.productId, ...(f.baseUrl.trim() ? { baseUrl: f.baseUrl.trim() } : {}) }
}

/** Environments grouped by product name (stable, alphabetical). */
export function groupByProduct<T extends { product: { id: string; name: string } }>(rows: T[]): { product: T['product']; rows: T[] }[] {
  const groups = new Map<string, { product: T['product']; rows: T[] }>()
  for (const r of rows) {
    const g = groups.get(r.product.id) ?? { product: r.product, rows: [] as T[] }
    g.rows.push(r)
    groups.set(r.product.id, g)
  }
  return [...groups.values()].sort((a, b) => a.product.name.localeCompare(b.product.name))
}

// ─── Tenant ──────────────────────────────────────────────────────────────────

export interface TenantLinkRow {
  environmentId: string
  externalTenantId: string
}

export interface TenantForm {
  name: string
  code: string
  timeZone: string
  location: string
  links: TenantLinkRow[]
}

export function emptyTenantForm(clientTimeZone: string): TenantForm {
  return { name: '', code: '', timeZone: clientTimeZone, location: '', links: [] }
}

export function tenantToForm(t: Tenant): TenantForm {
  return {
    name: t.name,
    code: t.code,
    timeZone: t.timeZone,
    location: t.location ?? '',
    links: t.environments.map((l) => ({ environmentId: l.environmentId, externalTenantId: l.externalTenantId ?? '' })),
  }
}

/** Upper-case code suggestion from a site name ("South Lake Union" → "SLU", "Rochester" → "ROC"). */
export function codeFromName(name: string): string {
  const words = name.trim().toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0].slice(0, 3)
  return words.map((w) => w[0]).join('').slice(0, 6)
}

export function validateTenant(f: TenantForm): Errors<'name' | 'code' | 'timeZone' | 'links'> {
  const e: Errors<'name' | 'code' | 'timeZone' | 'links'> = {}
  if (!f.name.trim()) e.name = E('required')
  if (!TENANT_CODE.test(f.code)) e.code = E('code')
  if (!isTimeZone(f.timeZone)) e.timeZone = E('timeZone')
  const ids = f.links.map((l) => l.environmentId)
  if (ids.some((id) => !id)) e.links = E('linkEnvironment')
  else if (new Set(ids).size !== ids.length) e.links = E('linkDuplicate')
  return e
}

export function tenantPayload(f: TenantForm): TenantInput {
  return {
    name: f.name.trim(),
    code: f.code,
    timeZone: f.timeZone.trim(),
    location: f.location.trim() || null,
    environments: f.links.map((l) => ({ environmentId: l.environmentId, externalTenantId: l.externalTenantId.trim() || null })),
  }
}

/** Create payload drops nulls the create DTO does not need. */
export function tenantCreatePayload(f: TenantForm): TenantInput {
  const p = tenantPayload(f)
  return { ...p, location: p.location ?? undefined }
}
